/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IOrderTemplateLineViewModel } from '../order-template';
import { IOrderTemplateData } from '../order-template.data';
import { IOrderTemplateProps } from '../order-template.props.autogenerated';
import { AddLineToTemplate } from '.';

interface IOrderTemplateBulkActionsProps extends IOrderTemplateProps<IOrderTemplateData> {
    orderTemplateId: string;
    selectedLines: IOrderTemplateLineViewModel[];
    isMinifiedView: boolean;
    isMultipleCatalogFeatureEnabledInHq: boolean;
    telemetryContent?: ITelemetryContent;
    onAddSelectionToBag(): void;
    onRemoveSelection(): void;
}

interface IOrderTemplateBulkActionsState {
    isAddDisabled: boolean;
    isRemoveDisabled: boolean;
}

/**
 * Bulk Order Template line actions.
 */
export class OrderTemplateBulkActions extends React.PureComponent<IOrderTemplateBulkActionsProps, IOrderTemplateBulkActionsState> {
    public state: IOrderTemplateBulkActionsState = {
        isAddDisabled: true,
        isRemoveDisabled: true
    };

    public static getDerivedStateFromProps(
        props: IOrderTemplateBulkActionsProps,
        state: IOrderTemplateBulkActionsState
    ): IOrderTemplateBulkActionsState {
        const selectedLines = props.selectedLines.length;
        let nextState = { ...state };
        nextState = {
            isAddDisabled: selectedLines === 0,
            isRemoveDisabled: selectedLines === 0
        };
        return nextState;
    }

    public render(): JSX.Element {
        const {
            addLineModalLinkText,
            searchButtonAriaLabel,
            searchInputAriaLabel,
            searchModalPlaceholderText,
            selectCatalogTitleText,
            selectProductButtonText,
            addItemToTemplateText,
            addLineProductUnitPricePrefix,
            backButtonText,
            decrementButtonAriaLabel,
            incrementButtonAriaLabel,
            quantitySelectLabel,
            addLineProductUnitOfMeasurePrefix,
            notFoundSearchErrorNotice,
            notFoundSearchErrorRedediation,
            searchErrorMessage,
            productDimensionTypeColor,
            productDimensionTypeConfiguration,
            productDimensionTypeSize,
            productDimensionTypeStyle,
            addSelectedToBagButtonText,
            removeSelectedToBagButtonText,
            searchResultsCountVerbage,
            searchResultsCountSubject,
            addToTemplateConfirmation,
            totalPriceLabel,
            progressNotificationText,
            addToTemplateDuplicateError,
            addToTemplateGenericError,
            dimensionMissingError
        } = this.props.resources;

        const addLineProps = {
            context: this.props.context,
            telemetryContent: this.props.telemetryContent,
            resources: {
                addLineModalLinkText,
                searchButtonAriaLabel,
                searchInputAriaLabel,
                searchModalPlaceholderText,
                selectCatalogTitleText,
                selectProductButtonText,
                addItemToTemplateText,
                addLineProductUnitPricePrefix,
                backButtonText,
                decrementButtonAriaLabel,
                incrementButtonAriaLabel,
                quantitySelectLabel,
                addLineProductUnitOfMeasurePrefix,
                notFoundSearchErrorNotice,
                notFoundSearchErrorRedediation,
                searchErrorMessage,
                productDimensionTypeColor,
                productDimensionTypeConfiguration,
                productDimensionTypeSize,
                productDimensionTypeStyle,
                searchResultsCountVerbage,
                searchResultsCountSubject,
                addToTemplateConfirmation,
                totalPriceLabel,
                progressNotificationText,
                addToTemplateDuplicateError,
                addToTemplateGenericError,
                dimensionMissingError
            },
            imageSettings: this.props.config.imageSettings,
            orderTemplateId: this.props.orderTemplateId,
            isMultipleCatalogFeatureEnabledInHq: this.props.isMultipleCatalogFeatureEnabledInHq,
            catalogs: this.props.data.catalogs?.result
        };

        return (
            <>
                <AddLineToTemplate {...addLineProps} />
                <Button
                    onClick={this.addHandler}
                    disabled={this.state.isAddDisabled}
                    className='add-selected-to-bag'
                    aria-label={addSelectedToBagButtonText}
                >
                    <span />
                    {!this.props.isMinifiedView && addSelectedToBagButtonText}
                </Button>
                <Button
                    onClick={this.removeHandler}
                    disabled={this.state.isRemoveDisabled}
                    className='remove-selected'
                    aria-label={removeSelectedToBagButtonText}
                >
                    <span />
                    {!this.props.isMinifiedView && removeSelectedToBagButtonText}
                </Button>
            </>
        );
    }

    private readonly addHandler = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({
            isAddDisabled: true
        });

        this.props.onAddSelectionToBag();

        this.setState({
            isAddDisabled: false
        });
    };

    private readonly removeHandler = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({
            isRemoveDisabled: true
        });

        this.props.onRemoveSelection();
    };
}
